import * as React from 'react';

import { enumUnit, type Unit } from '@rematter/frontend-lib-api-client';
import { Unit as ProtoUnit } from '@rematter/frontend-lib-api-client/grpc/types_pb';
import { atom, getDefaultStore, useAtomValue } from 'jotai';

import type { ScaleSidecar } from '~/shared/graphql/queries/useScalesQuery';
import { useHardwareSidecarConnection } from '~/shared/providers/libp2p/useHardwareSidecarConnection';
import { useScaleLocalClient } from '~/shared/providers/libp2p/useScaleLocalClient';
import { $globalScalesQuery } from '~/shared/state/scales.state';

export type LiveWeight = {
  grossWeight: number;
  tareWeight: number;
  netWeight: number;
  unitOfMeasurement: Unit | undefined;
  timestampMs: bigint;
  stable: boolean;
};

type ScaleWeightResult = {
  weight: LiveWeight | null;
  isConnecting: boolean;
  isConnected: boolean;
};

const protoUnitToUnit: Record<ProtoUnit, Unit | undefined> = {
  [ProtoUnit.UNSPECIFIED]: undefined,
  [ProtoUnit.POUNDS]: enumUnit.Pounds,
  [ProtoUnit.NET_TONS]: enumUnit.NetTons,
  [ProtoUnit.GROSS_TONS]: enumUnit.GrossTons,
  [ProtoUnit.KILOGRAMS]: enumUnit.Kilograms,
  [ProtoUnit.METRIC_TONS]: enumUnit.MetricTons,
  [ProtoUnit.SHORT_TONS]: enumUnit.ShortTons,
  [ProtoUnit.EACH]: enumUnit.Each,
  [ProtoUnit.SHORT_HUNDREDWEIGHT]: enumUnit.ShortHundredweight,
  [ProtoUnit.LONG_HUNDREDWEIGHT]: enumUnit.LongHundredweight,
  [ProtoUnit.YARD]: enumUnit.Yard,
  [ProtoUnit.METER]: enumUnit.Meter,
};

function toLiveWeight(update: {
  grossWeight: number;
  tareWeight: number;
  netWeight: number;
  unitOfMeasurement: ProtoUnit;
  timestampMs: bigint;
  stable: boolean;
}): LiveWeight {
  return {
    grossWeight: update.grossWeight,
    tareWeight: update.tareWeight,
    netWeight: update.netWeight,
    unitOfMeasurement: protoUnitToUnit[update.unitOfMeasurement],
    timestampMs: update.timestampMs,
    stable: update.stable,
  };
}

const $liveScaleWeight = atom<LiveWeight | null>(null);

export function useScaleWeight(
  scaleId: string | undefined,
  sidecar: ScaleSidecar | null | undefined,
): ScaleWeightResult {
  const { refetch: refetchScales } = useAtomValue($globalScalesQuery);
  const { connection, isConnecting } = useHardwareSidecarConnection({
    sidecar: sidecar ?? null,
    onConnectionFailed: refetchScales,
  });
  const scaleClient = useScaleLocalClient(sidecar?.id ?? '');
  const [weight, setWeight] = React.useState<LiveWeight | null>(null);

  const isConnected = connection?.connected ?? false;

  React.useEffect(() => {
    if (!scaleClient || !isConnected || !scaleId) return;

    let cancelled = false;
    const abort = new AbortController();
    const store = getDefaultStore();

    const updateWeight = (w: LiveWeight) => {
      setWeight(w);
      store.set($liveScaleWeight, w);
    };

    (async () => {
      try {
        const response = await scaleClient.getCurrentWeight({ scaleId });
        if (!cancelled && response.weight) {
          updateWeight(toLiveWeight(response.weight));
        }
      } catch {
        // Initial fetch failed, will rely on subscription
      }
    })();

    (async () => {
      try {
        for await (const update of scaleClient.subscribeWeight(
          { scaleId },
          { signal: abort.signal },
        )) {
          if (cancelled) return;
          updateWeight(toLiveWeight(update));
        }
      } catch (err) {
        if (
          cancelled ||
          (err instanceof DOMException && err.name === 'AbortError')
        ) {
          return;
        }
        console.warn('Scale weight subscription error', err);
      }
    })();

    return () => {
      cancelled = true;
      abort.abort();
      store.set($liveScaleWeight, null);
    };
  }, [scaleClient, isConnected, scaleId]);

  React.useEffect(() => {
    setWeight(null);
    getDefaultStore().set($liveScaleWeight, null);
  }, [scaleId]);

  return { weight, isConnecting, isConnected };
}

export function useScaleTakeWeight() {
  const takeWeight = React.useCallback(
    (): LiveWeight | null => getDefaultStore().get($liveScaleWeight),
    [],
  );

  return { takeWeight };
}
